import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTenantStore } from '@/store/tenantStore'
import { getTenantLogoUrl, getTenantId } from '@/utils/tenant'

export default function FindAccountPage() {
  const { tenantInfo } = useTenantStore()
  const { t } = useTranslation('auth')
  const tenantId = getTenantId()
  const logoUrl = getTenantLogoUrl(tenantId, tenantInfo?.logoUrl)

  return (
    <div className="flex justify-center items-center px-4 py-12 min-h-screen bg-gray-50 sm:px-6 lg:px-8">
      <div className="space-y-8 w-full max-w-md">
        {/* Header */}
        <div className="text-center">
          {logoUrl ? (
            <img src={logoUrl} alt={tenantInfo?.name || t('findAccount.logo')} className="mx-auto w-auto h-16" />
          ) : (
            <div
              className="flex justify-center items-center mx-auto w-16 h-16 text-2xl font-bold text-white rounded-xl"
              style={{ backgroundColor: tenantInfo?.primaryColor || '#6366f1' }}
            >
              {tenantInfo?.name?.charAt(0) || 'Q'}
            </div>
          )}
          <h2 className="mt-6 text-2xl font-bold text-gray-900">{t('findAccount.title')}</h2>
        </div>

        {/* TODO: 테넌트별 findAccountByPhone 설정 추가 시 조건부 렌더링 */}
        {/* Admin Contact Guidance */}
        <div className="p-8 space-y-6 bg-white rounded-lg shadow">
          {/* Info Icon */}
          <div className="flex justify-center">
            <div
              className="flex justify-center items-center w-16 h-16 rounded-full"
              style={{ backgroundColor: `${tenantInfo?.primaryColor || '#6366f1'}15` }}
            >
              <svg
                className="w-8 h-8"
                style={{ color: tenantInfo?.primaryColor || '#6366f1' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>

          {/* Main Message */}
          <div className="space-y-4 text-center">
            <h3 className="text-lg font-semibold text-gray-900">
              {t('findAccount.unknownEmail')}
            </h3>
            <p className="text-sm leading-relaxed text-gray-600">
              {t('findAccount.contactAdmin')}
            </p>
            <div className="pt-2">
              <p className="text-xs text-gray-500">
                {t('findAccount.forgotPasswordHint')}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Link
              to="/login"
              className="block px-4 py-3 w-full text-sm font-medium text-center text-white rounded-lg transition-colors bg-primary-600 hover:bg-primary-700"
              style={{ backgroundColor: tenantInfo?.primaryColor || undefined }}
            >
              {t('findAccount.backToLogin')}
            </Link>
            <Link
              to="/reset-password"
              className="block px-4 py-3 w-full text-sm font-medium text-center text-gray-700 rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
            >
              {t('findAccount.resetPassword')}
            </Link>
          </div>
        </div>

        {/* Bottom Navigation */}
        <div className="flex gap-4 justify-center text-sm">
          <Link to="/login" className="text-gray-500 hover:text-gray-700">{t('findAccount.login')}</Link>
          <span className="text-gray-300">|</span>
          <Link to="/reset-password" className="text-gray-500 hover:text-gray-700">{t('findAccount.resetPassword')}</Link>
        </div>
      </div>
    </div>
  )
}

/*
 * ============================================================================
 * LEGACY CODE: Phone-based Account Finder
 * ============================================================================
 * This code is preserved for future use when tenant-specific findAccountByPhone
 * setting is implemented. To re-enable:
 * 1. Add tenant setting check: if (tenantInfo?.settings?.findAccountByPhone)
 * 2. Replace admin guidance section with this form
 * 3. Restore useState and accountRecoveryApi imports
 * ============================================================================

import { useState } from 'react'
import { accountRecoveryApi } from '@/api/auth'

// Add to component:
const [name, setName] = useState('')
const [phoneNumber, setPhoneNumber] = useState('')
const [maskedEmail, setMaskedEmail] = useState<string | null>(null)
const [error, setError] = useState('')
const [isLoading, setIsLoading] = useState(false)

const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault()
  setError('')
  setMaskedEmail(null)
  setIsLoading(true)

  try {
    const response = await accountRecoveryApi.findAccount({ name, phoneNumber })
    setMaskedEmail(response.data.data!.maskedEmail)
  } catch (err: any) {
    const message = err.response?.data?.error?.message || '계정을 찾을 수 없습니다.'
    setError(message)
  } finally {
    setIsLoading(false)
  }
}

// Replace admin guidance with:
{maskedEmail ? (
  <div className="p-8 space-y-6 text-center bg-white rounded-lg shadow">
    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
      <p className="mb-1 text-sm text-green-700">등록된 이메일</p>
      <p className="text-lg font-semibold text-green-900">{maskedEmail}</p>
    </div>
    <Link
      to="/login"
      className="block px-4 py-3 w-full text-sm font-medium text-center text-white rounded-lg bg-primary-600 hover:bg-primary-700"
      style={{ backgroundColor: tenantInfo?.primaryColor || undefined }}
    >
      로그인하기
    </Link>
    <Link to="/reset-password" className="block text-sm text-primary-600 hover:text-primary-500">
      비밀번호를 잊으셨나요?
    </Link>
  </div>
) : (
  <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
    {error && (
      <div className="px-4 py-3 text-sm text-red-700 bg-red-50 rounded-lg border border-red-200">
        {error}
      </div>
    )}

    <div className="space-y-4">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">이름</label>
        <input
          id="name"
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="block px-4 py-3 mt-1 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="홍길동"
        />
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">휴대폰 번호</label>
        <input
          id="phone"
          type="tel"
          required
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          className="block px-4 py-3 mt-1 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="010-1234-5678"
        />
      </div>
    </div>

    <button
      type="submit"
      disabled={isLoading}
      className="flex justify-center px-4 py-3 w-full text-sm font-medium text-white rounded-lg border border-transparent shadow-sm bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
      style={{ backgroundColor: tenantInfo?.primaryColor || undefined }}
    >
      {isLoading ? '조회 중...' : '계정 찾기'}
    </button>
  </form>
)}

 * ============================================================================
 */
